import { useState, useEffect, useRef } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover, Tooltip } from "antd"
import { SyncOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons"
import { faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import dayjs from "dayjs"
import { TaskCalendarEvent } from "../scheduleApi"
import { HOUR_HEIGHT, MIN_EVENT_HEIGHT, snapTo15Minutes, getEventTopOffset, getEventHeight } from "./weekViewModel"
import { ELLIPSIS_TEXT_STYLE, CARD_COMPACT_MAX_HEIGHT, getCardOuterStyle, getCardInnerStyle } from "./weekViewCardStyles"
import WeekViewCardResizeHandle from "./WeekViewCardResizeHandle"
import TaskPopoverContent from "../TaskPopoverContent"
import { HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"

export type TaskColumn = {
  event: TaskCalendarEvent
  column: number
  totalColumns: number
}

export type Props = {
  taskColumn: TaskColumn
  onTaskClick?: (event: TaskCalendarEvent) => void
  onResize?: (event: TaskCalendarEvent, newDurationMinutes: number) => void
  onResizingChange?: (isResizing: boolean) => void
  topOffset: number
}

const UNASSIGNED_COLOR = '#e0e0e0'

const WeekViewTask = ({ taskColumn, onTaskClick, onResize, onResizingChange, topOffset }: Props) => {
  const { event, column, totalColumns } = taskColumn
  const color = event.crewForemanColor || UNASSIGNED_COLOR
  const isCompleted = !!event.completedAt

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `task-${event.id}`,
    data: { event, isTask: true },
    disabled: event.frozen,
  })

  const resizeId = `task-${event.id}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
    transform: resizeTransform,
  } = useDraggable({
    id: resizeId,
    data: { event, isResize: true, isTask: true },
    disabled: event.frozen,
  })

  const frozenIcon = event.frozen ? (
    <Tooltip title="Frozen">
      <FontAwesomeIcon icon={faSnowflake} style={{ color: '#0958d9', fontSize: 11, flexShrink: 0 }} />
    </Tooltip>
  ) : null

  const [isHovering, setIsHovering] = useState(false)
  const lastResizeTransformY = useRef<number>(0)

  const startTime = dayjs(event.start)
  const endTime = dayjs(event.end)
  const originalDurationMinutes = endTime.diff(startTime, 'minutes')

  const resizeDelta = resizeTransform?.y ?? 0

  useEffect(() => {
    if (resizeTransform?.y !== undefined) {
      lastResizeTransformY.current = resizeTransform.y
    }
  }, [resizeTransform?.y])

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      const deltaMinutes = (lastResizeTransformY.current / HOUR_HEIGHT) * 60
      const snappedDeltaMinutes = snapTo15Minutes(deltaMinutes)
      const newDuration = Math.max(15, originalDurationMinutes + snappedDeltaMinutes)
      if (onResize && newDuration !== originalDurationMinutes) {
        onResize(event, newDuration)
      }
      lastResizeTransformY.current = 0
    },
  })

  const formatTimeRange = () => {
    if (isResizing) {
      const deltaMinutes = (resizeDelta / HOUR_HEIGHT) * 60
      const snappedDeltaMinutes = snapTo15Minutes(deltaMinutes)
      const newDuration = Math.max(15, originalDurationMinutes + snappedDeltaMinutes)
      const newEndTime = startTime.add(newDuration, 'minute')
      return `${startTime.format('h:mm A')} - ${newEndTime.format('h:mm A')}`
    }
    return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`
  }

  const top = getEventTopOffset(event) - topOffset
  const baseHeight = getEventHeight(event)

  const snappedResizeDelta = isResizing
    ? (snapTo15Minutes((resizeDelta / HOUR_HEIGHT) * 60) / 60) * HOUR_HEIGHT
    : 0
  const height = Math.max(MIN_EVENT_HEIGHT, baseHeight + snappedResizeDelta)

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  const widthPercent = totalColumns > 0 ? 100 / totalColumns : 100
  const leftPercent = totalColumns > 0 ? column * widthPercent : 0

  const isCompact = baseHeight < CARD_COMPACT_MAX_HEIGHT

  const outerStyle: React.CSSProperties = {
    ...getCardOuterStyle({ top, height, widthPercent, leftPercent, isDragging, isResizing, transform }),
    backgroundColor: color,
  }
  const innerStyle = getCardInnerStyle({ isCompact })

  return (
    <Popover
      content={<TaskPopoverContent event={event} />}
      open={isHovering && !isDragging && !isResizing}
      placement="right"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (!isResizing && onTaskClick) {
            onTaskClick(event)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={outerStyle}
      >
        <div style={innerStyle}>
        {isCompact ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
            <FontAwesomeIcon icon={faCalendarCheck} style={{ color: '#999', fontSize: 10, flexShrink: 0 }} />
            <span
              style={{
                ...ELLIPSIS_TEXT_STYLE,
                fontWeight: 500,
                fontSize: 11,
                flex: 1,
                minWidth: 0,
                textDecoration: isCompleted ? 'line-through' : undefined,
                color: isCompleted ? '#999' : undefined,
              }}
            >
              {event.title}
            </span>
            {frozenIcon}
            {event.repeatingId && (
              <SyncOutlined style={{ color: '#999', fontSize: '10px', flexShrink: 0 }} />
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#999', fontSize: 10, lineHeight: 1.2 }}>
              <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: 10 }} />
              <span>TASK</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  ...ELLIPSIS_TEXT_STYLE,
                  fontWeight: 500,
                  fontSize: 12,
                  flex: 1,
                  minWidth: 0,
                  textDecoration: isCompleted ? 'line-through' : undefined,
                  color: isCompleted ? '#999' : undefined,
                }}
              >
                {event.title}
              </span>
              {frozenIcon}
              {event.repeatingId && (
                <SyncOutlined style={{ color: '#999', fontSize: '11px', flexShrink: 0 }} />
              )}
            </div>
            <span style={{ ...ELLIPSIS_TEXT_STYLE, color: '#666', fontSize: 11 }}>
              {formatTimeRange()}
            </span>
          </>
        )}

        {(isHovering || isResizing) && onResize && (
          <WeekViewCardResizeHandle
            setNodeRef={setResizeNodeRef}
            listeners={resizeListeners}
            attributes={resizeAttributes}
            color={color}
          />
        )}
        </div>
      </div>
    </Popover>
  )
}

export default WeekViewTask
